import { Button, Form, Modal, Spinner } from "react-bootstrap";
import {
  ESTADOS_DE_ANIMO,
  MascotaReporteDiario,
  QUERY_KEY_OBTENER_MASCOTAS_REPORTE_DIARIO,
  crearReporteDiario,
  esFotoValida,
  esVideoValido,
} from "../../../services/reporteDiarioServicios";
import { Controller, useForm } from "react-hook-form";
import { Model, defaultValues, generarNuevoReporteDiario } from "./Model";
import { Temperamento } from "../../../services/mascotasServicios";
import SuccessButton from "../../organisms/SuccessButton";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import ServerErrorModal from "../../organisms/ServerErrorModal";
import { useState } from "react";
import { bytesToMegas } from "../../../utils/numberUtils";
import { getDataUrl } from "../../../utils/fileUtils";

function CrearReporteModal({
  mascota,
  onCloseClick,
}: {
  mascota: MascotaReporteDiario;
  onCloseClick: () => void;
}) {
  const queryClient = useQueryClient();
  const {
    handleSubmit,
    control,
    formState: { isValid },
    watch,
    setValue,
  } = useForm<Model>({
    defaultValues: { ...defaultValues },
    mode: "all",
  });
  const mutacionGuardarReporte = useMutation(crearReporteDiario, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_OBTENER_MASCOTAS_REPORTE_DIARIO],
      });
      onCloseClick();
    },
  });
  const [fotoGeneralError, setFotoGeneralError] = useState<string | undefined>(
    undefined
  );
  const [fotoNovedadesError, setFotoNovedadesError] = useState<
    string | undefined
  >(undefined);
  const [videoNovedadesError, setVideoNovedadesError] = useState<
    string | undefined
  >(undefined);

  const fotoGeneralUrl = watch("fotoGeneralUrl");
  const fotoNovedadesUrl = watch("fotoNovedadesUrl");
  const videoNovedadesUrl = watch("videoNovedadesUrl");
  const aplicaDesayunoValue = watch("aplicaDesayuno");
  const aplicaAlmuerzoValue = watch("aplicaAlmuerzo");
  const aplicaCenaValue = watch("aplicaCena");
  const aplicaMedicamentosValue = watch("aplicaMedicamentos");

  const onSubmit = async (model: Model) => {
    const req = await generarNuevoReporteDiario(model, mascota.idAsistencia);
    mutacionGuardarReporte.mutate(req);
  };

  const loadFotoGeneralUrl = async (file: File) => {
    const dataUrl = await getDataUrl(file);
    setValue("fotoGeneralUrl", dataUrl);
  };

  const loadFotoNovedadesUrl = async (file: File) => {
    const dataUrl = await getDataUrl(file);
    setValue("fotoNovedadesUrl", dataUrl);
  };

  const loadVideoNovedadesUrl = async (file: File) => {
    const dataUrl = await getDataUrl(file);
    setValue("videoNovedadesUrl", dataUrl);
  };

  const onFotoNovedadesChange: React.ChangeEventHandler = (event) => {
    setFotoNovedadesError(undefined);
    if (!event) {
      return;
    }
    const target = event!.target! as HTMLInputElement;
    const files = target.files;
    if (files) {
      const selectedFile = files[0];
      if (!esFotoValida(selectedFile.name)) {
        setFotoNovedadesError(
          "Solo se permiten imágenes de novedades png y jpeg"
        );
        return;
      }

      if (bytesToMegas(selectedFile.size) > 64) {
        setFotoNovedadesError("Solo se permiten archivos menores a 64MB");
        return;
      }
      setValue("fotoNovedades", selectedFile);
      loadFotoNovedadesUrl(selectedFile);
    }
  };

  const onFotoGeneralChange: React.ChangeEventHandler = (event) => {
    setFotoGeneralError(undefined);
    if (!event) {
      return;
    }
    const target = event!.target! as HTMLInputElement;
    const files = target.files;
    if (files) {
      const selectedFile = files[0];
      if (!esFotoValida(selectedFile.name)) {
        setFotoGeneralError(
          "Solo se permiten archivos jpeg y png para foto general"
        );
        return;
      }

      if (bytesToMegas(selectedFile.size) > 64) {
        setFotoGeneralError("Solo se permiten archivos menores a 64MB");
        return;
      }
      setValue("fotoGeneral", selectedFile);
      loadFotoGeneralUrl(selectedFile);
    }
  };
  const onVideoNovedadesChange: React.ChangeEventHandler = (event) => {
    setVideoNovedadesError(undefined);
    if (!event) {
      return;
    }
    const target = event!.target! as HTMLInputElement;
    const files = target.files;
    if (files) {
      const selectedFile = files[0];
      if (!esVideoValido(selectedFile.name)) {
        setVideoNovedadesError(
          "Solo se permiten archivos mp4 y 3gp para video de novedades"
        );
        return;
      }

      if (bytesToMegas(selectedFile.size) > 64) {
        setVideoNovedadesError("Solo se permiten archivos menores a 64MB");
        return;
      }

      setValue("videoNovedades", selectedFile);
      loadVideoNovedadesUrl(selectedFile);
    }
  };

  const tieneErroresArchivos =
    fotoGeneralError || fotoNovedadesError || videoNovedadesError;

  if (mutacionGuardarReporte.isError) {
    return <ServerErrorModal onClose={onCloseClick} />;
  }

  return (
    <Modal show={true}>
      <Form className="w-100" onSubmit={handleSubmit(onSubmit)}>
        <Modal.Header className="d-flex justify-content-center">
          <h4 className="text-center">Registro de reporte diario</h4>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex flex-column">
            <section>
              <div className="mb-3">
                <h5>
                  <strong>Datos de mascota</strong>
                </h5>
              </div>
              <div>
                <p>
                  Nombre: <i>{mascota.nombreMascota}</i>
                </p>
                <p>
                  Propietario: <i>{mascota.nombrePropietario}</i>
                </p>
              </div>
            </section>

            {fotoGeneralUrl && (
              <img src={fotoGeneralUrl} width="250" height="300" alt="" />
            )}

            <Form.Group controlId="formFotoGeneral" className="mb-3">
              <Form.Label>Foto general</Form.Label>
              <Form.Control
                type="file"
                size="sm"
                onChange={(event) => {
                  onFotoGeneralChange(event);
                }}
                accept="image/jpeg,image/png"
              />
            </Form.Group>

            {fotoGeneralError && (
              <small className="text-danger">{fotoGeneralError}</small>
            )}

            <section className="mb-2">
              <div className="mb-3 mt-2">
                <h5>
                  <strong>Alimentación</strong>
                </h5>
              </div>
              <div
                id="seccionesAlimentacion"
                className="d-flex flex-column gap-3"
              >
                <section id="desayunoSection">
                  <Form.Group controlId="formAplicaDesayuno" className="mb-3">
                    <Controller
                      control={control}
                      name="aplicaDesayuno"
                      render={({ field: { onChange, value, ref } }) => (
                        <Form.Switch
                          label="Aplica desayuno"
                          onChange={onChange}
                          checked={!!value}
                          ref={ref}
                        />
                      )}
                    />
                  </Form.Group>
                  {aplicaDesayunoValue && (
                    <Form.Group controlId="formDesayuno" className="mb-3">
                      <Controller
                        control={control}
                        name="desayuno"
                        render={({ field: { onChange, value, ref } }) => (
                          <Form.Check
                            label="Desayuno"
                            onChange={onChange}
                            checked={!!value}
                            ref={ref}
                          />
                        )}
                      />
                    </Form.Group>
                  )}
                </section>

                <section id="almuerzoSection">
                  <Form.Group controlId="formAplicaAlmuerzo" className="mb-3">
                    <Controller
                      control={control}
                      name="aplicaAlmuerzo"
                      render={({ field: { onChange, value, ref } }) => (
                        <Form.Switch
                          label="Aplica almuerzo"
                          onChange={onChange}
                          checked={!!value}
                          ref={ref}
                        />
                      )}
                    />
                  </Form.Group>
                  {aplicaAlmuerzoValue && (
                    <Form.Group controlId="formAlmuerzo" className="mb-3">
                      <Controller
                        control={control}
                        name="almorzo"
                        render={({ field: { onChange, value, ref } }) => (
                          <Form.Check
                            label="Almuerzo"
                            onChange={onChange}
                            checked={!!value}
                            ref={ref}
                          />
                        )}
                      />
                    </Form.Group>
                  )}
                </section>

                <section id="cenaSection">
                  <Form.Group controlId="formAplicaCena" className="mb-3">
                    <Controller
                      control={control}
                      name="aplicaCena"
                      render={({ field: { onChange, value, ref } }) => (
                        <Form.Switch
                          label="Aplica cena"
                          onChange={onChange}
                          checked={!!value}
                          ref={ref}
                        />
                      )}
                    />
                  </Form.Group>
                  {aplicaCenaValue && (
                    <Form.Group controlId="formCeno" className="mb-3">
                      <Controller
                        control={control}
                        name="ceno"
                        render={({ field: { onChange, value, ref } }) => (
                          <Form.Check
                            label="Ceno"
                            onChange={onChange}
                            checked={!!value}
                            ref={ref}
                          />
                        )}
                      />
                    </Form.Group>
                  )}
                </section>
              </div>

              <Form.Group
                controlId="formObservacionesAlimenticias"
                className="mb-3"
              >
                <Form.Label>Observaciones alimenticias</Form.Label>
                <Controller
                  control={control}
                  name="observacionesAlimenticias"
                  rules={{ required: false }}
                  render={({ field: { onChange, value, ref } }) => (
                    <Form.Control
                      rows={3}
                      as="textarea"
                      onChange={onChange}
                      value={value}
                      ref={ref}
                    />
                  )}
                />
              </Form.Group>
            </section>
            <section>
              <div className="mb-3 mt-2">
                <h5>
                  <strong>Salud</strong>
                </h5>
              </div>
              <Form.Group controlId="formAplicaMedicamentos" className="mb-3">
                <Controller
                  control={control}
                  name="aplicaMedicamentos"
                  render={({ field: { onChange, value, ref } }) => (
                    <Form.Switch
                      label="Aplica medicamentos"
                      onChange={onChange}
                      checked={!!value}
                      ref={ref}
                    />
                  )}
                />
              </Form.Group>
              {aplicaMedicamentosValue && (
                <Form.Group controlId="formTomoMedicamentos" className="mb-3">
                  <Controller
                    control={control}
                    name="tomoMedicamentos"
                    render={({ field: { onChange, value, ref } }) => (
                      <Form.Check
                        label="Tomó medicamentos"
                        onChange={onChange}
                        checked={!!value}
                        ref={ref}
                      />
                    )}
                  />
                </Form.Group>
              )}

              <Form.Group
                controlId="formObservacionesMedicamentos"
                className="mb-3"
              >
                <Form.Label>Observaciones médicas</Form.Label>
                <Controller
                  control={control}
                  name="observacionesMedicas"
                  render={({ field: { onChange, value, ref } }) => (
                    <Form.Control
                      rows={3}
                      as="textarea"
                      onChange={onChange}
                      value={value}
                      ref={ref}
                    />
                  )}
                />
              </Form.Group>
            </section>
            <section>
              <div className="mb-3 mt-2">
                <h5>
                  <strong>Comportamiento</strong>
                </h5>
              </div>
              <Form.Group controlId="formTemperamento" className="mb-3">
                <Form.Label>Temperamento</Form.Label>
                <Controller
                  control={control}
                  name="temperamento"
                  rules={{ required: true }}
                  render={({ field: { onChange, ref } }) => (
                    <>
                      <Form.Select
                        ref={ref}
                        onChange={onChange}
                        required={true}
                        defaultValue={defaultValues.temperamento}
                      >
                        {Object.keys(Temperamento).map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </Form.Select>
                    </>
                  )}
                />
              </Form.Group>
              <Form.Group
                controlId="formObservacionesSocializacion"
                className="mb-3"
              >
                <Form.Label>Observaciones de socialización</Form.Label>
                <Controller
                  control={control}
                  name="observacionesSocializacion"
                  rules={{ required: false }}
                  render={({ field: { onChange, value, ref } }) => (
                    <Form.Control
                      rows={3}
                      as="textarea"
                      onChange={onChange}
                      value={value}
                      ref={ref}
                    />
                  )}
                />
              </Form.Group>
              <Form.Group controlId="formEstadoAnimo" className="mb-3">
                <Form.Label>Estado de ánimo</Form.Label>
                <Controller
                  control={control}
                  name="estadoAnimo"
                  rules={{ required: false }}
                  render={({ field: { onChange, ref } }) => (
                    <>
                      <Form.Select
                        ref={ref}
                        onChange={onChange}
                        required={true}
                        defaultValue={defaultValues.estadoAnimo}
                      >
                        {ESTADOS_DE_ANIMO.map((e) => (
                          <option key={e} value={e}>
                            {e}
                          </option>
                        ))}
                      </Form.Select>
                    </>
                  )}
                />
              </Form.Group>
            </section>
            <section>
              <div className="mb-3 mt-2">
                <h5>
                  <strong>Novedades</strong>
                </h5>
              </div>

              <Form.Group controlId="formNovedades" className="mb-3">
                <Form.Label>Detalles de novedades</Form.Label>
                <Controller
                  control={control}
                  name="novedades"
                  rules={{ required: false }}
                  render={({ field: { onChange, value, ref } }) => (
                    <Form.Control
                      rows={3}
                      as="textarea"
                      onChange={onChange}
                      value={value}
                      ref={ref}
                    />
                  )}
                />
              </Form.Group>

              {fotoNovedadesUrl && (
                <img src={fotoNovedadesUrl} width="250" height="300" alt="" />
              )}

              <Form.Group controlId="formFotoNovedades" className="mb-3">
                <Form.Label>Foto de novedades</Form.Label>
                <Form.Control
                  type="file"
                  size="sm"
                  onChange={(event) => {
                    onFotoNovedadesChange(event);
                  }}
                  accept="image/jpeg,image/png"
                />
              </Form.Group>

              {fotoNovedadesError && (
                <small className="text-danger">{fotoNovedadesError}</small>
              )}

              {videoNovedadesUrl && (
                <div>
                  <video width="250" height="300" controls>
                    <source src={videoNovedadesUrl} />
                  </video>
                </div>
              )}

              <Form.Group controlId="formVideoNovedades" className="mb-3">
                <Form.Label>Video de novedades</Form.Label>
                <Form.Control
                  type="file"
                  size="sm"
                  onChange={(event) => {
                    onVideoNovedadesChange(event);
                  }}
                  accept="video/mp4,video/3gpp"
                />
              </Form.Group>

              {videoNovedadesError && (
                <small className="text-danger">{videoNovedadesError}</small>
              )}
            </section>
          </div>
          <div className="d-flex justify-content-end gap-2">
            <SuccessButton
              type="submit"
              disabled={!!tieneErroresArchivos || !isValid}
            >
              {mutacionGuardarReporte.isLoading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                "Guardar"
              )}
            </SuccessButton>
            <Button variant="danger" onClick={onCloseClick}>
              Cerrar
            </Button>
          </div>
        </Modal.Body>
      </Form>
    </Modal>
  );
}

export default CrearReporteModal;
